import { PublicKey } from "@solana/web3.js";
import { useFormikContext } from "formik";
import { FC, useEffect, useMemo, useState } from "react";
import {
  EMPTY_STRING,
  MESSAGE_TYPE,
} from "../../../common/constants/common.constants";
import {
  ICreateClubWizardForm,
  ICreateClubWizardProps,
} from "../../../common/interfaces/clubs-wizard.interface";
import { ICollectionData } from "../../../common/interfaces/common.interface";
import {
  checkIfCollectionIsInitialised,
  getCollectionMintMetadata,
} from "../../../solana/common/helpers/nftClubs.helpers";
import { createNotification } from "../../../utilities/notification";
import WizardField from "../../WizardField/WizardField";
import WizardHeader from "../WizardHeader/WizardHeader";
import CollectionData from "./CollectionData/CollectionData";
import "./NftConfiguration.scss";

const NftConfiguration: FC<ICreateClubWizardProps> = ({ formFields }) => {
  const { values, setFieldValue, setFieldError } =
    useFormikContext<ICreateClubWizardForm>();

  const [collectionData, setCollectionData] = useState<ICollectionData>();

  useEffect(() => {
    void getCollectionMintData();
    setFieldValue(formFields.nftMintAddress.name, values.collectionMintAddress);
  }, [values.collectionMintAddress]);

  const getCollectionMintData = async () => {
    try {
      if (values.collectionMintAddress !== EMPTY_STRING) {
        try {
          new PublicKey(values.collectionMintAddress);
        } catch (error) {
          setFieldValue(formFields.isCollectionMintValid.name, false);
          return;
        }
        const loadedData = await getCollectionMintMetadata(
          new PublicKey(values.collectionMintAddress)
        );

        if (loadedData && loadedData.collectionAddress) {
          if (!loadedData.isVerified) {
            setFieldError(
              formFields.nftMintAddress.name,
              "Collection not verified!"
            );
            setFieldValue(formFields.isCollectionMintValid.name, false);
            return;
          }
          setFieldValue(
            formFields.nftMintAddress.name,
            loadedData.collectionAddress
          );
          const collectionInfo = await getCollectionMintMetadata(
            new PublicKey(loadedData.collectionAddress)
          );
          collectionInfo
            ? setFieldValue(formFields.collectionData.name, collectionInfo)
            : setFieldValue(formFields.collectionData.name, loadedData);
          collectionInfo
            ? setCollectionData(collectionInfo)
            : setCollectionData(loadedData);
          !collectionInfo && setCollectionData(loadedData);
        } else {
          const existingCollection = await checkIfCollectionIsInitialised(
            values.collectionMintAddress
          );
          if (existingCollection) {
            setCollectionData(loadedData);
            setFieldValue(formFields.isCollectionMintValid.name, true);
            setFieldValue(
              formFields.nftMintAddress.name,
              values.collectionMintAddress
            );
            return;
          }
          setFieldValue(formFields.isCollectionMintValid.name, false);
          return;
        }
      }
      setFieldValue(formFields.isCollectionMintValid.name, true);
    } catch (error: any) {
      setFieldValue(formFields.isCollectionMintValid.name, false);
      setCollectionData(undefined);
      createNotification(
        MESSAGE_TYPE.ERROR,
        "Problem with loading collection metadata!"
      );
    }
  };

  const showCollectionData = useMemo(() => {
    return (
      collectionData &&
      values.collectionMintAddress !== EMPTY_STRING &&
      values.isCollectionMintValid
    );
  }, [collectionData, values]);

  return (
    <div className="nft-configuration">
      <div className="nft-configuration__item">
        <WizardHeader description="Here you can define the collection your club will be using for voting and membership purposes." />
        <WizardField
          type="text"
          name={formFields.collectionMintAddress.name}
          placeholder="Type your collection mint address here"
          label={formFields.collectionMintAddress.label}
        />
        <p className="nft-configuration__item-tip">
          Find your mint address by opening your NFT on Solscan → Metadata →
          Root → Collection → Key
        </p>
      </div>
      {collectionData?.collectionAddress && (
        <p className="nft-configuration__collection-address">
          You have selected NFT from collection with address:
          {collectionData?.collectionAddress!}
        </p>
      )}
      {showCollectionData && (
        <CollectionData collectionData={collectionData!} />
      )}
      <div className="nft-configuration__item">
        <WizardHeader
          title="Total votes"
          description="Define the maximum amount of votes available for the club’s proposals. Note that not all users will be active members. This can cause an inability to achieve the defined voting quorum for proposals."
        />
        <WizardField
          type="number"
          name={formFields.totalNftVotes.name}
          placeholder="Type total votes available here"
          label={formFields.totalNftVotes.label}
        />
        <p className="nft-configuration__item-tip">
          1 NFT= 1 VOTE, one member can have more NFTs, more votes
        </p>
      </div>
    </div>
  );
};

export default NftConfiguration;
